import { ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import { MoreVertical, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Button from "../../common/components/Button";
import ConfirmationModal from "../../common/components/ConfirmationModal";

/**
 * Dropdown menu component for asset actions (Edit, Delete)
 * @param {function} onEdit - Handler for edit action
 * @param {function} onDelete - Handler for delete action (from useAsset hook)
 * @param {boolean} deleting - Loading state for delete operation
 */
const AssetActionsDropdown = ({ onEdit, onDelete, deleting }) => {
  const { t } = useTranslation("assets");
  const [anchorEl, setAnchorEl] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const navigate = useNavigate();
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEditClick = () => {
    handleClose();
    if (onEdit) {
      onEdit();
    }
  };

  const handleDeleteClick = () => {
    handleClose();
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (onDelete) {
      await onDelete();
      setDeleteModalOpen(false);
      // Navigate back to assets list after successful deletion
      navigate("/assets");
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
  };

  return (
    <>
      <Button
        onClick={handleClick}
        variant="secondary"
        sx={{ minWidth: "unset", px: 1.3 }}
        data-testid="asset-actions-dropdown-button"
      >
        <MoreVertical size={20} />
      </Button>

      <Menu
        id="asset-actions-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        data-testid="asset-actions-menu"
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            minWidth: 180,
            mt: 1,
          },
        }}
      >
        <MenuItem onClick={handleEditClick} data-testid="asset-action-edit">
          <ListItemIcon>
            <Pencil size={18} />
          </ListItemIcon>
          <ListItemText primary={t("actions.edit")} />
        </MenuItem>

        <MenuItem onClick={handleDeleteClick} data-testid="asset-action-delete">
          <ListItemIcon>
            <Trash2 size={18} />
          </ListItemIcon>
          <ListItemText primary={t("actions.delete")} />
        </MenuItem>
      </Menu>

      <ConfirmationModal
        open={deleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t("deleteModal.title")}
        message={t("deleteModal.message")}
        confirmText={t("deleteModal.confirm")}
        cancelText={t("deleteModal.cancel")}
        confirmColor="error"
        loading={deleting}
      />
    </>
  );
};

export default AssetActionsDropdown;
